import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Share2, Quote } from "lucide-react";
import { toast } from "sonner";

interface QuoteSharePillProps {
  articleTitle: string;
}

export function QuoteSharePill({ articleTitle }: QuoteSharePillProps) {
  const [selectedText, setSelectedText] = useState("");
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setSelectedText("");
        setPosition(null);
        return;
      }

      const text = selection.toString().trim();
      if (text.length < 5 || text.length > 500) {
        setSelectedText("");
        setPosition(null);
        return;
      }

      // Check if selection is within article body
      const articleEl = document.getElementById("article-content");
      if (!articleEl || !articleEl.contains(selection.anchorNode)) {
        setSelectedText("");
        setPosition(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      // Position pill centered above the selection
      const x = rect.left + rect.width / 2;
      const y = rect.top + window.scrollY - 10;

      setSelectedText(text);
      setPosition({ x, y });
    };

    document.addEventListener("selectionchange", handleSelection);
    return () => {
      document.removeEventListener("selectionchange", handleSelection);
    };
  }, []);

  const handleCopyQuote = () => {
    if (!selectedText) return;
    const url = window.location.href;
    const citation = `"${selectedText}"\n\n— from "${articleTitle}" on Bloxida (${url})`;

    navigator.clipboard.writeText(citation).then(() => {
      setCopied(true);
      toast.success("Quote & citation copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShareX = () => {
    if (!selectedText) return;
    const url = window.location.href;
    const tweetText = `"${selectedText.slice(0, 180)}${selectedText.length > 180 ? "…" : ""}"`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}&via=Bloxida`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  if (!position || !selectedText) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={pillRef}
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 5, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: "translate(-50%, -100%)",
        }}
        className="fixed z-40 flex items-center gap-1.5 p-1 bg-background/95 backdrop-blur-xl border border-border/80 rounded-full shadow-2xl pointer-events-auto"
      >
        <button
          onClick={handleCopyQuote}
          className="h-8 px-3 rounded-full hover:bg-accent text-xs font-semibold text-foreground flex items-center gap-1.5 transition-colors"
          title="Copy quote with formatted citation"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-primary" />
              <span className="text-primary font-bold">Copied!</span>
            </>
          ) : (
            <>
              <Quote className="h-3.5 w-3.5 text-primary" />
              <span>Copy Quote</span>
            </>
          )}
        </button>

        <div className="h-4 w-px bg-border/60" />

        <button
          onClick={handleShareX}
          className="h-8 px-2.5 rounded-full hover:bg-accent text-xs font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          title="Share quote to X"
        >
          <Share2 className="h-3.5 w-3.5" />
          <span>Share</span>
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
